import React from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';

export type MultiState = 'pending' | 'inProgress' | 'completed' | 'approvedEmpty';

export interface MultiStateButtonProps {
  isActive: boolean;
  isApproved: boolean;
  onChange: (next: { isActive: boolean; isApproved: boolean }) => void;
  disabled?: boolean;
  isApprovedButEmptyValue?: boolean;
}

function getState(isActive: boolean, isApproved: boolean, isApprovedButEmptyValue?: boolean): MultiState {
  if (isApprovedButEmptyValue) return 'approvedEmpty';
  if (!isActive) return 'pending';
  if (isActive && !isApproved) return 'inProgress';
  if (isActive && isApproved) return 'completed';
  return 'pending';
}

function getNextState(isActive: boolean, isApproved: boolean): { isActive: boolean; isApproved: boolean } {
  if (!isActive) return { isActive: true, isApproved: false };
  if (isActive && !isApproved) return { isActive: true, isApproved: true };
  if (isActive && isApproved) return { isActive: false, isApproved: false };
  return { isActive: false, isApproved: false };
}

function getLabel(state: MultiState) {
  switch (state) {
    case 'pending': return 'Pendiente';
    case 'inProgress': return 'En Proceso';
    case 'completed': return 'Completado';
    case 'approvedEmpty': return 'Aprobado sin valor';
    default: return '';
  }
}

function getShortLabel(state: MultiState) {
  switch (state) {
    case 'pending': return 'P';
    case 'inProgress': return 'E';
    case 'completed': return 'C';
    case 'approvedEmpty': return 'A';
    default: return '';
  }
}

function getColor(state: MultiState): 'inherit' | 'warning' | 'success' {
  switch (state) {
    case 'pending': return 'inherit';
    case 'inProgress': return 'warning';
    case 'completed': return 'success';
    case 'approvedEmpty': return 'inherit';
    default: return 'inherit';
  }
}

const MultiStateButton: React.FC<MultiStateButtonProps> = ({ isActive, isApproved, onChange, disabled, isApprovedButEmptyValue }) => {
  const state = getState(isActive, isApproved, isApprovedButEmptyValue);
  const handleClick = () => {
    if (disabled) return;
    const next = getNextState(isActive, isApproved);
    onChange(next);
  };
  return (
    <Tooltip title={getLabel(state)}>
      <span>
        <Button
          variant="contained"
          color={getColor(state)}
          size="small"
          onClick={handleClick}
          disabled={disabled}
          sx={{
            minWidth: 32, width: 32, height: 32, p: 0,
            ...(state === 'approvedEmpty' && {
              backgroundColor: '#b3e5fc',
              color: '#01579b',
              '&:hover': { backgroundColor: '#81d4fa' },
            })
          }}
        >
          {getShortLabel(state)}
        </Button>
      </span>
    </Tooltip>
  );
};

export default MultiStateButton; 